import axios from 'axios';

export const DEFAULT_API_BASE_URL =
  import.meta.env.VITE_EMAIL_VERIFICATION_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname
    ? `${window.location.protocol}//${window.location.hostname}:4100`
    : 'http://localhost:4100');

const STORAGE_KEYS = {
  apiBaseUrl: 'evs_api_base_url',
};

export function getApiBaseUrl() {
  return localStorage.getItem(STORAGE_KEYS.apiBaseUrl) || DEFAULT_API_BASE_URL;
}

export function setStoredApiBaseUrl(url) {
  if (url) localStorage.setItem(STORAGE_KEYS.apiBaseUrl, url.replace(/\/+$/, ''));
  else localStorage.removeItem(STORAGE_KEYS.apiBaseUrl);
}

/** Derives the WebSocket origin (ws:// or wss://) from the current API base URL. */
export function getWsBaseUrl() {
  const base = getApiBaseUrl();
  try {
    const url = new URL(base);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return url.origin;
  } catch {
    return base.replace(/^http/, 'ws');
  }
}

export class ApiError extends Error {
  constructor(message, { status, errors, isAuthError, isUnreachable } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
    this.isAuthError = isAuthError;
    // The request never reached the service (refused, DNS failure, timeout, CORS). Retrying is
    // pointless until something changes, and the fix is always "start/point at the service".
    this.isUnreachable = isUnreachable;
  }
}

// A dashboard request should fail while the user is still watching. Single-address verification
// legitimately takes longer (it opens an SMTP conversation), so verifyEmail() raises its own
// timeout rather than every list endpoint waiting a minute for a service that is not there.
export const DEFAULT_TIMEOUT_MS = 15000;
export const VERIFY_TIMEOUT_MS = 60000;

export const apiClient = axios.create({
  timeout: DEFAULT_TIMEOUT_MS,
  // The verification service authenticates with the dashboard's own session cookie -- it issues no
  // key of its own -- so every request has to carry credentials. The service must therefore be
  // served on the same site as the dashboard for the browser to attach that cookie.
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  config.baseURL = getApiBaseUrl();
  return config;
});

let authErrorHandler = null;
/** Registered once by the app shell so a 401 anywhere raises the auth banner. */
export function setAuthErrorHandler(fn) {
  authErrorHandler = fn;
}

// Every endpoint returns { success, data } or { success:false, message, errors? }.
// Unwrap on success; normalize into ApiError on failure so callers never touch the envelope.
apiClient.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && typeof body === 'object' && !(body instanceof Blob) && 'success' in body) {
      return body.success ? body.data : body;
    }
    return body;
  },
  (error) => {
    const status = error.response?.status;
    let body = error.response?.data;
    // When responseType:'blob' was requested but the server returned a JSON error body,
    // axios still hands it back as a Blob -- surface a generic message rather than [object Blob].
    if (body instanceof Blob) body = undefined;
    const isUnreachable = !error.response;
    const message = isUnreachable
      ? `Cannot reach the verification service at ${getApiBaseUrl()}. Check that the service is running and that VITE_EMAIL_VERIFICATION_API_URL points at it.`
      : body?.message || error.message || 'Request failed';
    const isAuthError = status === 401;
    const apiError = new ApiError(message, { status, errors: body?.errors, isAuthError, isUnreachable });
    if (isAuthError && authErrorHandler) authErrorHandler(message);
    return Promise.reject(apiError);
  }
);
